import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type SettingsModalSource } from '@/models/analytics/features/settings';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import {
  DEFAULT_SETTINGS_MODAL_ROUTE,
  lastSettingsRouteAtom,
  settingsModalAtom,
  settingsModalSourceAtom,
  type SettingsModalState,
} from '../atoms/settingsModalAtom';
import {
  preloadSettingsScreen,
  SCREENS,
} from '../components/SettingsModal/settingsScreenLoaders';

/**
 * Tracks whether the most recent `open()` call passed an explicit href.
 * Mobile uses this to decide whether to land on the section view (explicit)
 * or the list view (implicit) when the drawer first opens.
 */
const settingsModalOpenedWithRouteAtom = atom(false);

/**
 * `Date.now()` of the most recent open. Used to compute the `duration_ms`
 * property on the `SettingsModalClosed` analytics event. Cleared on close.
 */
const settingsModalOpenedAtAtom = atom<number | null>(null);

export const splitSettingsModalHref = (
  input: string
): { route: string; hash?: string } => {
  const hashIndex = input.indexOf('#');
  if (hashIndex === -1) {
    return { route: input };
  }
  return {
    route: input.slice(0, hashIndex),
    hash: input.slice(hashIndex),
  };
};

/**
 * Single source of truth for the `SettingsModalSectionChanged` analytics
 * event shape. Both `useSettingsModal#setRoute` and
 * `useNavigateInSettingsModalOrRouter#navigateInModal` call through this
 * helper so the event name + props shape only live in one place.
 *
 * The helper used to take the caller's dispatch vehicle, because the two
 * hooks deliberately sourced it differently (Provider `useDispatch()` vs the
 * Redux store singleton, so the Provider-less path stayed unit-testable).
 * `track()` is source-agnostic, so that parameter — and the split it existed
 * to paper over — is gone.
 *
 * No-op when `from === to` so no-op route writes (e.g. clicking the
 * already-active sidebar item) don't pollute analytics.
 */
export const emitSettingsModalSectionChanged = (from: string, to: string): void => {
  if (from === to) return;
  track({
    event_name: TrackingEventName.SettingsModalSectionChanged,
    props: { from, to },
  });
};

/**
 * Updates modal route + hash in Jotai only (no Redux). Shared with
 * `useNavigateInSettingsModalOrRouter` so settings screens can redirect
 * inside the modal without requiring a react-redux `<Provider>` in unit
 * tests. Callers that need analytics should fire events after calling this.
 */
export const useApplySettingsModalHref = () => {
  const setModalState = useSetAtom(settingsModalAtom);
  const setPersistedLastRoute = useSetAtom(lastSettingsRouteAtom);
  const setOpenedWithRoute = useSetAtom(settingsModalOpenedWithRouteAtom);

  return useCallback(
    (href: string) => {
      const { route, hash } = splitSettingsModalHref(href);
      // Synchronous preload before state commit so the lazy chunk download
      // races with React's render/commit work. Webpack dedupes if the
      // Suspense boundary later requests the same module specifier.
      preloadSettingsScreen(route);
      setModalState((prev) => ({ ...prev, route, hash }));
      setPersistedLastRoute(route);
      setOpenedWithRoute(true);
    },
    [setModalState, setPersistedLastRoute, setOpenedWithRoute]
  );
};

/**
 * Imperative API for the global settings modal.
 *
 * - `open(href?, source?)` opens the modal at the given href, or resumes the
 *   last-viewed section. `source` describes which entry point invoked the
 *   open and feeds the `SettingsModalOpened` analytics event.
 * - `setRoute(href)` updates the active section while the modal is open.
 * - `close()` closes the modal but keeps the last route persisted.
 *
 * Each call also fires its corresponding analytics event via
 * `track()`. URL-driven opens (mount with
 * `?settings=<slug>`, legacy redirect) come in via the same `open(href, source)`
 * path with the appropriate `source` value, so analytics has a single fire path.
 */
export const useSettingsModal = () => {
  const [state, setState] = useAtom(settingsModalAtom);
  const setPersistedLastRoute = useSetAtom(lastSettingsRouteAtom);
  const persistedLastRoute = useAtomValue(lastSettingsRouteAtom);
  const [openedWithRoute, setOpenedWithRoute] = useAtom(settingsModalOpenedWithRouteAtom);
  const [openedAt, setOpenedAt] = useAtom(settingsModalOpenedAtAtom);
  const setModalSource = useSetAtom(settingsModalSourceAtom);
  const applySettingsModalHref = useApplySettingsModalHref();

  // Refs let `close` read the latest atom values without re-binding the
  // callback on every state change. Without this, `close` would capture
  // `state.route` / `openedAt` via closure and could log a stale value if
  // React batches a state update with the close trigger.
  const stateRef = useRef(state);
  stateRef.current = state;
  const openedAtRef = useRef(openedAt);
  openedAtRef.current = openedAt;

  const open = useCallback(
    (href?: string, source: SettingsModalSource = 'sidebar') => {
      // Resume path only: drop a persisted last-route that no longer maps to
      // a shipped section (renamed/removed) so we don't briefly mount a
      // missing screen + rely on `UnknownRouteRecovery` to redirect a frame
      // later. Explicit hrefs are trusted. `SCREENS` (static) over
      // `useSettingsList` (hook-heavy) because this runs from the always-
      // mounted sidebar footer; flag/role-gated routes that still exist in
      // `SCREENS` aren't caught here (or anywhere central) — they rely on
      // per-screen access checks.
      const isResume = href === undefined;
      const candidate = href ?? persistedLastRoute;
      const candidateRoute = splitSettingsModalHref(candidate).route;
      const target =
        isResume && SCREENS[candidateRoute] == null
          ? DEFAULT_SETTINGS_MODAL_ROUTE
          : candidate;
      const { route, hash } = splitSettingsModalHref(target);
      // Kick off the lazy chunk fetch synchronously, before React commits the
      // open state. The chunk download then races with React's mount work
      // (Suspense boundary creation, URL sync, analytics dispatch) instead of
      // queueing behind it. Webpack dedupes if Suspense ends up requesting
      // the same module a few ms later.
      preloadSettingsScreen(route);
      setState({ open: true, route, hash });
      setPersistedLastRoute(route);
      setOpenedWithRoute(href !== undefined);
      setOpenedAt(Date.now());
      setModalSource(source);

      track({
        event_name: TrackingEventName.SettingsModalOpened,
        props: { source, route },
      });
    },
    [
      persistedLastRoute,
      setState,
      setPersistedLastRoute,
      setOpenedWithRoute,
      setOpenedAt,
      setModalSource,
    ]
  );

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));

    const openedAtSnapshot = openedAtRef.current;
    track({
      event_name: TrackingEventName.SettingsModalClosed,
      props: {
        last_route: stateRef.current.route,
        duration_ms: openedAtSnapshot ? Date.now() - openedAtSnapshot : 0,
      },
    });
    setOpenedAt(null);
    setModalSource(undefined);
  }, [setState, setOpenedAt, setModalSource]);

  const setRoute = useCallback(
    (href: string) => {
      const { route } = splitSettingsModalHref(href);
      // Read from the ref rather than `state.route` so two rapid
      // `setRoute` calls across separate React events (e.g. a quick
      // double-click on different sidebar items) compute `previousRoute`
      // against the latest committed atom value, not whichever closure
      // value React happened to capture. Mirrors the `close()` pattern.
      // Also lets us drop `state.route` from the dep array, stabilising
      // the callback identity for downstream `useEffect` consumers.
      const previousRoute = stateRef.current.route;
      applySettingsModalHref(href);
      emitSettingsModalSectionChanged(previousRoute, route);
    },
    [applySettingsModalHref]
  );

  /**
   * Mobile-only: return to the sidebar list view without closing the drawer.
   * Desktop callers should not need this — both panes are visible.
   */
  const goToMobileList = useCallback(() => {
    setOpenedWithRoute(false);
  }, [setOpenedWithRoute]);

  return {
    state,
    open,
    close,
    setRoute,
    openedWithRoute,
    goToMobileList,
  } satisfies {
    state: SettingsModalState;
    open: (href?: string, source?: SettingsModalSource) => void;
    close: () => void;
    setRoute: (href: string) => void;
    openedWithRoute: boolean;
    goToMobileList: () => void;
  };
};
